import { useMemo } from "react";
import { useQuery } from "react-query";
import useGlobalContext from "hooks/globalData";
import contextStore from "stores/contextStore";
import { SkuAvailabilityId } from "types/commonTypes";
import {
  AttributesFilterModel,
  CatalogAttributesFilterRequest,
  GenericSkuFilterModel,
} from "services/organizationPortalClient";
import {
  AttributeGroupModel,
  AttributesFilter as SharedAttributesFilter,
} from "@knowfully/ui.common";
import { customAttributeFilterComponents } from "./CustomAttributeFilters";

export type AttributesFilterProps = {
  skuAvailabilityIds?: SkuAvailabilityId[];
  value: string[];
  onChange: (value: string[]) => void;
};

export default function AttributesFilter({
  skuAvailabilityIds,
  value,
  onChange,
}: AttributesFilterProps) {
  const { catalogFilterService } = useGlobalContext();
  const { organization } = contextStore.useState();

  const { data, isLoading } = useQuery(
    [
      "catalogFilterService.getAttributesFilterModel",
      organization?.id,
      ...(skuAvailabilityIds ?? []),
    ],
    () =>
      catalogFilterService.getAttributesFilterModel(
        CatalogAttributesFilterRequest.fromJS({ skuAvailabilityIds })
      )
  );

  const groups: AttributeGroupModel[] = useMemo(
    () =>
      (data?.data ?? []).map((g: AttributesFilterModel) => ({
        id: g.organizationAttributeTypeId,
        displayName: g.organizationAttributeTypeDisplayName ?? "",
        attributeTypeId: g.attributeTypeId,
        options: (g.attributes ?? [])
          .filter((a: GenericSkuFilterModel) => !!a.id)
          .map((a: GenericSkuFilterModel) => ({
            id: a.id,
            label: a.value ?? "",
            metaValues: a.metaValues?.map((m) => ({
              name: m.name ?? "",
              value: m.value ?? "",
            })),
          })),
      })),
    [data]
  );

  return (
    <SharedAttributesFilter
      groups={groups}
      value={value}
      onChange={onChange}
      customRegistry={customAttributeFilterComponents}
      isLoading={isLoading}
    />
  );
}
